import { BrandTheme } from '../types/theme';

export const DEFAULT_THEME: BrandTheme = {
  brand: {
    name: "Hotel Guest Portal",
    hotelLogo: undefined,
    hotelName: undefined,
    colors: {
      primary: "#2563eb",
      secondary: "#64748b",
      cta: "#f59e0b",
      ctaText: "#b45309",
      border: "#e5e7eb",
      gradient: {
        start: "#f8fafc",
        middle: "#e2e8f0",
        end: "#f8fafc"
      },
      text: {
        primary: "#1e293b",
        secondary: "#475569",
        light: "#94a3b8",
        disabled: "#9ca3af"
      },
      surface: {
        base: "#FFFFFF",
        subtle: "#FAFAFA",
        warm: "#FEF3E2",
        hover: "#FEF7ED",
        border: "#E0E0E0",
        borderSubtle: "#F0F0F0"
      }
    },
    fonts: {
      primary: "Inter, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif",
      secondary: "system-ui, -apple-system, 'Segoe UI', sans-serif"
    },
    shadows: {
      light: "0 2px 8px rgba(37, 99, 235, 0.15)",
      medium: "0 4px 16px rgba(37, 99, 235, 0.1)"
    }
  },
  userInterface: {
    topBar: {
      background: "#ffffff",
      text: "#1e293b",
      borderBottom: "#e2e8f0"
    },
    userMessage: {
      background: "#eff6ff",
      text: "#1e3a8a",
      border: "#3b82f6",
      shadow: "0 2px 8px rgba(59, 130, 246, 0.15)"
    },
    inputBox: {
      inactive: {
        background: "#f8fafc",
        text: "#1e293b",
        border: "#d1d5db",
        placeholder: "#475569"
      },
      active: {
        background: "#ffffff",
        text: "#1e293b",
        border: "#3b82f6",
        shadow: "0 0 0 3px rgba(59, 130, 246, 0.1)"
      }
    },
    sendButton: {
      background: "#f59e0b",
      text: "#ffffff",
      border: "#f59e0b",
      shadow: "0 2px 8px rgba(245, 158, 11, 0.2)",
      hover: {
        background: "#d97706",
        text: "#ffffff",
        border: "#d97706",
        shadow: "0 4px 12px rgba(245, 158, 11, 0.3)"
      },
      disabled: {
        background: "#9ca3af",
        text: "#ffffff",
        border: "#9ca3af"
      }
    }
  },
  aiResponseSystem: {
    responseCard: {
      background: "#ffffff",
      border: "#e5e7eb",
      shadow: "0 4px 16px rgba(37, 99, 235, 0.1)",
      borderRadius: "12px",
      padding: {
        default: "32px",
        large: "40px"
      }
    },
    avatar: {
      size: "32px",
      borderRadius: "50%",
      shadow: "0 2px 8px rgba(37, 99, 235, 0.2)",
      border: "2px solid #ffffff",
      image: {
        objectFit: "cover",
        width: "100%",
        height: "100%"
      }
    },
    contentTypes: {
      text: {
        color: "#1e293b",
        fontSize: "15px",
        lineHeight: "1.6"
      },
      highlight: {
        background: "#eff6ff",
        border: "#bfdbfe",
        borderLeft: "#2563eb",
        text: "#1e3a8a",
        icon: "🔒",
        padding: "14px 16px",
        borderRadius: "8px"
      },
      feature: {
        background: "#fffbeb",
        border: "#fed7aa",
        borderLeft: "#f59e0b",
        text: "#92400e",
        icon: "⭐",
        padding: "14px 16px",
        borderRadius: "8px"
      },
      warning: {
        background: "#fef2f2",
        border: "#fecaca",
        borderLeft: "#ef4444",
        text: "#991b1b",
        icon: "⚠️",
        padding: "14px 16px",
        borderRadius: "8px"
      },
      success: {
        background: "#f0fdf4",
        border: "#bbf7d0",
        borderLeft: "#22c55e",
        text: "#166534",
        icon: "✅",
        padding: "14px 16px",
        borderRadius: "8px"
      },
      details: {
        background: "#f8fafc",
        border: "#e5e7eb",
        borderRadius: "8px",
        title: {
          background: "#f8fafc",
          text: "#1e293b",
          fontSize: "14px",
          fontWeight: "600",
          padding: "12px 16px",
          borderBottom: "#e5e7eb"
        },
        content: {
          background: "#ffffff",
          text: "#1e293b",
          fontSize: "14px",
          lineHeight: "1.7",
          padding: "16px"
        }
      },
      image: {
        borderRadius: "12px",
        shadow: "0 4px 12px rgba(37, 99, 235, 0.1)",
        caption: {
          background: "#f8fafc",
          text: "#475569",
          fontSize: "13px",
          padding: "12px",
          borderTop: "#e5e7eb"
        }
      },
      video: {
        borderRadius: "12px",
        shadow: "0 4px 12px rgba(37, 99, 235, 0.1)",
        background: "#ffffff",
        border: "#e5e7eb",
        controls: {
          background: "#000000",
          color: "#ffffff"
        },
        caption: {
          background: "#f8fafc",
          text: "#475569",
          fontSize: "13px",
          padding: "12px",
          borderTop: "#e5e7eb"
        }
      },
      weather: {
        background: "#fffbeb",
        border: "#fed7aa",
        borderLeft: "#f59e0b",
        text: "#92400e",
        icon: "☀️",
        padding: "16px",
        borderRadius: "8px"
      },
      suggestionCard: {
        grid: "2x2",
        maxCards: 5,
        gap: "10px",
        background: "#f8fafc",
        border: "1px solid rgba(100, 116, 139, 0.2)",
        borderRadius: "0",
        padding: "16px 18px",
        minHeight: "72px",
        accentBar: {
          width: "3px",
          hoverWidth: "4px",
          color: "#2563eb"
        },
        hover: {
          background: "#f1f5f9",
          border: "1px solid #2563eb",
          shadow: "0 4px 12px rgba(37, 99, 235, 0.15)"
        },
        focus: {
          outline: "3px solid #2563eb",
          outlineOffset: "2px"
        },
        iconRing: {
          size: "36px",
          background: "#ffffff",
          border: "1px solid #2563eb",
          iconSize: "16px",
          iconColor: "#2563eb",
          hoverIconColor: "#1e40af",
          hoverBorderColor: "#1e40af"
        },
        actionLabel: {
          fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          fontSize: "13px",
          fontWeight: "600",
          color: "#1e293b"
        },
        hintLabel: {
          fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          fontSize: "11px",
          color: "#64748b"
        },
        sectionLabel: {
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: "11px",
          fontWeight: "500",
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: "0.18em"
        }
      }
    }
  },
  aiAssistant: {
    name: "Assistant",
    avatar: {
      size: "32px",
      gradient: {
        start: "#8b5cf6",
        end: "#ec4899"
      },
      fallback: {
        letter: "A",
        backgroundColor: "#2563eb"
      },
      border: "2px solid #ffffff",
      shadow: "0 2px 8px rgba(139, 92, 246, 0.2)"
    },
    nameStyle: {
      color: "#1e293b",
      fontWeight: "700",
      fontSize: "14px"
    },
    timestamp: {
      color: "#94a3b8",
      fontSize: "12px"
    }
  },
  accessibility: {
    focusOutline: "#fbbf24",
    focusOutlineWidth: "3px",
    focusOutlineOffset: "2px",
    minTouchTarget: "44px"
  }
} as any;